import React from "react";
import { useMealCatalogue } from "../../hooks/useMealCatalogue";

interface Schedule {
  flightNumber: string | number;
  airlineCode?: string;
  departure: string | { airport: string; [key: string]: any };
  departureAirport?: string;
  arrival: string | { airport: string; [key: string]: any };
  arrivalAirport?: string;
  departureTime?: string;
  arrivalTime?: string;
  cabinTypeName?: string;
  elapsedTime?: number;
}

interface TicketProps {
  flightData: any;
  passenger: {
    firstName: string;
    lastName: string;
    seat?: string;
  };
  ticketNumber: string | number;
  journey?: {
    departureDate: string;
    departureTime: string;
    firstAirportCode: string;
    lastAirportCode: string;
    numberOfFlights: number;
    arrivalDate?: string;
    arrivalTime?: string;
  };
}

const Ticket: React.FC<TicketProps> = ({
  flightData,
  passenger,
  ticketNumber,
  journey,
}) => {
  const { mealCodes, mealLabel } = useMealCatalogue();
  console.log("Flight data:", JSON.stringify(flightData, null, 2));
  const onward = flightData?.onwardFlight;
  const schedules: Schedule[] = onward?.schedules || [];
  const airlineCode = flightData?.validatingCarrier;
  const airlineLogo = `/assets/airlines/${airlineCode}.png`;

  // Use journey dates if provided, else fallback to first/last schedule
  const departureDate = journey?.departureDate || schedules[0]?.departure;
  const departureTime = journey?.departureTime || schedules[0]?.departureTime;
  const arrivalDate =
    journey?.arrivalDate || schedules[schedules.length - 1]?.arrival || "";
  const arrivalTime =
    journey?.arrivalTime || schedules[schedules.length - 1]?.arrivalTime || "";

  // Stops logic (already handled)
  const revalidated = flightData?.revalidatedData || "";
  const stops =
    revalidated[0]?.onward?.stops ??
    revalidated[0]?.onwardFlight?.stops ??
    null;

  return (
    <div className="w-full max-w-4xl mx-auto bg-card rounded-3xl p-6 flex flex-col md:flex-row items-stretch shadow-lg mb-8 font-sans">
      {/* Left blue bar */}
      <div className="w-4 bg-primary rounded-l-3xl mr-6 hidden md:block" />
      {/* Main ticket content */}
      <div className="flex-1 flex flex-col md:flex-row items-stretch">
        {/* Left section */}
        <div className="flex-1 flex flex-col justify-between">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center">
            {/* Departure info */}
            <div className="text-center md:text-left">
              <div className="text-base text-heading mb-1">
                {onward?.departureCity || "From"}
              </div>
              <div className="text-4xl font-bold tracking-tightest">
                {journey?.firstAirportCode || onward?.departureAirport || "-"}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {onward?.departureTerminalName || ""}
              </div>
              <div className="text-cardsmall text-gray-500 mt-2">
                Departure
                <br />
                {typeof departureDate === "object"
                  ? departureDate.date || ""
                  : departureDate}
                {departureTime ? `-${departureTime}` : ""}
              </div>
            </div>
            {/* Middle: All flight logos and numbers in journey */}
            <div className="flex flex-col items-center mx-8">
              <div className="text-base text-heading mb-1">
                {onward?.bookingDate}
              </div>
              <div className="font-semibold text-small mb-1">
                {schedules[0]?.elapsedTime
                  ? `${Math.floor((schedules[0].elapsedTime || 0) / 60)}h ${
                      (schedules[0].elapsedTime || 0) % 60
                    }m`
                  : ""}
              </div>
              <div className="text-cardsmall  text-gray-500 mb-5">
                {typeof stops === "number"
                  ? stops === 0
                    ? "Direct"
                    : `${stops} Stop${stops > 1 ? "s" : ""}`
                  : ""}
              </div>
              {/* Stack all flights in journey horizontally: logo above, code bold, number below in gray */}
              <div className="flex flex-row items-end gap-8 my-4">
                {schedules.map((seg: Schedule, idx: number) => (
                  <div key={idx} className="flex flex-col items-center">
                    <img
                      src={`/assets/airlines/${
                        seg.airlineCode || airlineCode
                      }.png`}
                      alt={seg.airlineCode || airlineCode}
                      className="h-8 mb-1"
                      style={{ objectFit: "contain" }}
                    />
                    <div className="font-bold text-base leading-tight">
                      {seg.airlineCode || airlineCode}
                    </div>
                    <div className="text-cardsmall text-gray-500 leading-tight">
                      {seg.flightNumber}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Arrival info */}
            <div className="text-center md:text-right">
              <div className="text-base text-heading mb-1">
                {onward?.arrivalCity || "To"}
              </div>
              <div className="text-4xl font-bold tracking-tightest">
                {onward?.arrivalAirport || journey?.lastAirportCode || "-"}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {onward?.arrivalTerminalName || " "}
              </div>
              <div className="text-cardsmall text-gray-500 mt-2">
                Arrival
                <br />
                {typeof arrivalDate === "object"
                  ? arrivalDate.date || ""
                  : arrivalDate}
                {arrivalTime ? `-${arrivalTime}` : ""}
              </div>
            </div>
          </div>
          <div className="mt-4 text-cardbase text-heading flex items-center justify-between">
            <span>
              Booking ID:{" "}
              <span className="font-bold">
                {flightData?.pnr || `YD${ticketNumber}`}
              </span>
            </span>
            {/* Flight segments horizontally stacked, only if stops > 0 */}
            {typeof stops === "number" && stops > 0 && (
              <div className="flex flex-col gap-1 items-center">
                {schedules.map((seg: Schedule, idx: number) => {
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-1 text-cardbase text-heading px-1 py-1 rounded border border-gray-200 bg-white/60"
                    >
                      {/* Departure and Arrival airport codes */}
                      <span className="font-bold uppercase flex flex-col gap-1">
                        <span className="text-primary">
                          {/* Departure airport code */}
                          {typeof seg.departure === "object"
                            ? seg.departure.airport
                            : seg.departureAirport}
                          {" - "}
                        </span>
                        <span className="text-primary">
                          {/* Arrival airport code */}
                          {typeof seg.arrival === "object"
                            ? seg.arrival.airport
                            : seg.arrivalAirport}
                          {" - "}
                        </span>
                      </span>

                      <span className="text-gray-500 flex flex-col gap-1">
                        {/* Departure date/time */}
                        <span>
                          {typeof seg.departure === "object"
                            ? `${seg.departure.date || ""} - ${seg.departure.time || ""}`
                            : `${seg.departure || ""} - ${seg.departureTime || ""}`}
                        </span>
                        {/* <span>---</span> */}
                        {/* Arrival date/time */}
                        <span>
                          {typeof seg.arrival === "object"
                            ? `${seg.arrival.date || ""} - ${seg.arrival.time || ""}`
                            : `${seg.arrival || ""} - ${seg.arrivalTime || ""}`}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {/* Divider */}
        <div className="w-px bg-gray-300 mx-6 hidden md:block" />
        {/* Right section */}
        <div className="flex flex-col justify-between min-w-[220px]">
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-8 w-16 bg-black rounded-sm" />
              <div className="text-cardsmall font-bold">
                PNR: {flightData?.pnr || "—"}
              </div>
            </div>
            <div className="text-cardsmall font-bold mb-2">
              Ticket No: {flightData?.ticketNumber || "—"}
            </div>
            <div className="text-small mb-2">
              <span className="font-bold">Passenger</span>
              <span className="ml-6">Seat</span>
            </div>
            <div className="text-base font-semibold mb-2">
              {passenger.firstName} {passenger.lastName}
              <span className="ml-6">{passenger.seat || "-"}</span>
            </div>
            <div className="text-small mb-2">
              <span className="font-bold">Class</span>
              <span className="ml-6">Flight No.</span>
            </div>
            <div className="text-base font-semibold mb-2">
              {schedules[0]?.cabinTypeName || "-"}
              <span className="ml-6 flex flex-col items-end">
                {/* Other flights in the journey, if any */}
                {schedules.map((seg: Schedule, idx: number) => (
                  <span key={idx}>
                    {seg.airlineCode || airlineCode} {seg.flightNumber}
                  </span>
                ))}
              </span>
            </div>
            {/* Meal Preference from Special Services */}
            {flightData?.specialServices && flightData.specialServices.length > 0 && (() => {
              // From the server's catalogue, not a copy. This list used to be
              // hardcoded here and had already drifted four codes behind, so a
              // passenger who ordered one of those saw a raw code on their
              // ticket. The fetch is cached for the session by the hook.
              const mealServices = flightData.specialServices.filter(
                (s: any) => mealCodes.has(s.code)
              );
              if (mealServices.length === 0) return null;
              return (
                <div className="text-cardsmall mb-2">
                  <span className="font-bold">Meal: </span>
                  {mealServices.map((m: any, i: number) => (
                    <span key={i} className="font-medium">
                      {m.message || mealLabel.get(m.code) || m.code}
                      {i < mealServices.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ticket;
